<script setup>
import { storeToRefs } from "pinia";
import useStoreCotizaciones from "@/store/cotizaciones";
import Multiselect from "vue-multiselect";
import useCotizacion from "@/composables/useCotizacion";
import ArgonSwitch from "@/components/ArgonSwitch.vue";
import { onUnmounted } from "vue";

const useStoreCotizacion = useStoreCotizaciones();

const {
  cambiarValorIncluyeSeguro,
  fetchFormInfoCotizacion,
  submitCotizacion,
} = useStoreCotizacion;

const {
  arrendamiento,
  arrendamientos,
  btnCargando,
  cliente,
  clientes,
  consultandoInfo,
  descripcion,
  enganche,
  enganches,
  incluyeSeguro,
  importe,
  linea,
  moneda,
  rentas,
  residual,
  residuales,
  seguro,
} = storeToRefs(useStoreCotizacion);

fetchFormInfoCotizacion();

onUnmounted(() => {
  cliente.value = "";
  enganche.value = "";
  residual.value = "";
  arrendamiento.value = "";
  importe.value = "";
  rentas.value = "";
  seguro.value = "";
  linea.value = "";
  moneda.value = "";
  descripcion.value = "";
  incluyeSeguro.value = false;
});
</script>

<template>
  <div class="row position-relative">
    <div
      v-if="consultandoInfo"
      class="h-100 bg-white rounded d-flex justify-content-center align-items-center position-absolute"
      style="opacity: 0.7; z-index: 100"
    >
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
    <form
      class="row"
      @submit.prevent="submitCotizacion"
      method="POST"
      id="formCotizacion"
    >
      <div class="form-group col-md-12 position-relative">
        <label for="cliente">Cliente*:</label>
        <multiselect
          v-model="cliente"
          :options="clientes"
          :value="[clientes]"
          :searchable="true"
          label="label"
          track-by="id"
          required
          placeholder="Seleccione"
          :object="true"
          :disabled="consultandoInfo"
        ></multiselect>
        <router-link
          :to="{ name: 'crear-cliente' }"
          class="btn position-absolute end-0 top-0 btn-link btn-sm pt-1 pe-3"
        >
          <i class="ri-add-line me-1"></i>
          Nuevo cliente
        </router-link>
      </div>
      <div class="form-group col-md-6">
        <label for="enganche">Enganche*:</label>
        <select
          name="enganche"
          class="form-control"
          id="enganche"
          v-model="enganche"
          :disabled="consultandoInfo"
          required
        >
          <option value="">Seleccione</option>
          <option
            :value="row.porcentaje"
            v-for="row in enganches"
            :key="row.id"
          >
            {{ row.porcentaje }}%
          </option>
        </select>
      </div>
      <div class="form-group col-md-6">
        <label for="residual">Residual*:</label>
        <select
          name="residual"
          class="form-control"
          id="residual"
          v-model="residual"
          :disabled="consultandoInfo"
          required
        >
          <option value="">Seleccione</option>
          <option :value="row.residual" v-for="row in residuales" :key="row.id">
            {{ row.residual }}%
          </option>
        </select>
      </div>
      <div class="form-group col-md-12">
        <label for="importe">Precio activo (Con IVA)*:</label>
        <input
          type="tel"
          name="importe"
          id="importe"
          autocomplete="off"
          class="form-control ps-2"
          placeholder="00.00"
          v-model="importe"
          required
        />
      </div>
      <div class="form-group col-md-12">
        <label for="cliente">Tipo arrendamiento*:</label>
        <select
          name="arrendamiento"
          id="arrendamiento"
          class="form-control"
          v-model="arrendamiento"
          :disabled="consultandoInfo"
          required
          @change="cambiarValorIncluyeSeguro"
        >
          <option value="">Seleccione</option>
          <option :value="row.id" v-for="row in arrendamientos" :key="row.id">
            {{ row.nombre }}
          </option>
        </select>
      </div>
      <div class="form-group col-md-6" v-show="arrendamiento == 2">
        <label>Incluye seguro*:</label>
        <div class="mt-1">
          <argon-switch
            :checked="incluyeSeguro"
            @change="incluyeSeguro = !incluyeSeguro"
          />
        </div>
      </div>
      <div class="form-group col-md-6">
        <label for="rentas">Rentas*:</label>
        <select name="rentas" class="form-control" id="rentas" v-model="rentas">
          <option value="">Seleccione</option>
          <option :value="val" v-for="(val, index) in 8" :key="index">
            {{ val }}
          </option>
        </select>
      </div>
      <div class="form-group col-md-6" v-show="arrendamiento != 2">
        <label for="seguro">Seguro*:</label>
        <input
          type="tel"
          class="form-control"
          autocomplete="off"
          name="seguro"
          id="seguro"
          placeholder="00.00"
          v-model="seguro"
        />
      </div>
      <div class="form-group col-md-6">
        <label for="linea">Línea*:</label>
        <select
          name="linea"
          class="form-control"
          id="linea"
          v-model="linea"
          required
        >
          <option value="">Seleccione</option>
          <option value="Azul">Azul</option>
          <option value="Platino">Platino</option>
        </select>
      </div>
      <div class="form-group col-md-6">
        <label for="moneda">Moneda*:</label>
        <select
          name="moneda"
          class="form-control"
          id="moneda"
          v-model="moneda"
          required
        >
          <option value="">Seleccione</option>
          <option value="MXN">MXN</option>
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
        </select>
      </div>
      <div class="form-group col-md-12">
        <label for="descripcion">Descripción*:</label>
        <textarea
          name="descripcion"
          id="descripcion"
          class="form-control"
          rows="2"
          v-model="descripcion"
        ></textarea>
      </div>
      <div class="col-md-12 mt-1 text-end">
        <button class="btn btn-primary btn-sm me-1" type="submit">
          <span
            class="spinner-border spinner-border-sm me-1"
            role="status"
            aria-hidden="true"
            v-if="btnCargando"
          ></span>
          Cotizar
        </button>
        <button class="btn btn-danger btn-sm" :disabled="btnCargando">
          Limpiar
        </button>
      </div>
    </form>
  </div>
</template>
